<template>
  <div class="sharing-view">
    <div v-if="isShowEmptyScreen" class="container">
      <EmptyQuizAnswer
        lookbackTab="slides"
      />
    </div>
    <div v-else class="container">
      <div class="sharing-view-content">
        <div class="iframe-placement">
          <iframe
            id="shared-presentation"
            frameborder="0"
            :src="sharingViewUrl"
            ref="sharingViewIframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            allowfullscreen
            @load="onSharingViewLoad"
          />
        </div>
      </div>
      <div class="sharing-view-actions navigator">
        <span
          class="action"
          :class="{
            disabled: isPrevSlideDisabled
          }"
          @click="onClickPreviousSlide"
        >
          <aha-icon
            class="prev"
            name="arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
        </span>
        <span class="number">{{ currentSharingSlideIndex + 1 }}</span>
        <span
          class="action"
          :class="{
            disabled: isNextSlideDisabled
          }"
          @click="onClickNextSlide"
        >
          <aha-icon
            class="next"
            name="arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
        </span>
      </div>
      <CTAAttendedPresentation />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Util from '@/util'
import Config from '@/config'
import storage from '@/services/storage'
import EmptyQuizAnswer from './EmptyQuizAnswer.vue'
import CTAAttendedPresentation from '../attended-presentations/CTAAttendedPresentation.vue'

const EVENT_CHANGE_SLIDE = 'template-preview:change-slide'

export default {
  components: {
    EmptyQuizAnswer,
    CTAAttendedPresentation,
  },
  props: {
    presentation: {
      type: Object,
      required: true,
    },
    slides: {
      type: Array,
      required: true,
    },
    maxLookbackSlideIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentSharingSlideIndex: 0,
      isSharingViewLoaded: false,
    }
  },
  computed: {
    isPrevSlideDisabled() {
      return !this.isSharingViewLoaded || this.currentSharingSlideIndex === 0
    },
    isNextSlideDisabled() {
      if (!this.isSharingViewLoaded) return true

      return this.currentSharingSlideIndex >= this.maxLookbackSlideIndex
    },
    sharingViewUrl() {
      const {
        slugifiedName: slug, shareCode, planType, user,
      } = this.presentation
      const whiteLabelPresenterDomain = user && user.whiteLabelPresenterDomain
      const params = {
        tracking: false,
        hideText: true,
        hideAccessCode: true,
        hideSlideNavigation: true,
        pureContent: true,
        swipeControl: true,
      }

      const paramString = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join('&')

      let baseUrl = Config.presenterUrl
      let path = `/share/${shareCode}`

      if (
        Util.checkWhiteLabelDomain()
        && whiteLabelPresenterDomain
        && planType === 'white-label'
      ) {
        baseUrl = `https://${whiteLabelPresenterDomain}`
      } else if (slug && shareCode && shareCode.indexOf('-') > -1) {
        path = `/share/${slug}-${shareCode}`
      }

      return `${baseUrl}${path}?${paramString}`
    },
    isShowEmptyScreen() {
      return this.maxLookbackSlideIndex === -1
    },
  },
  mounted() {
    window.addEventListener('message', (event) => {
      if (event.data.direction === 'left') {
        this.changeSlide(this.currentSharingSlideIndex + 1, true)
      } else if (event.data.direction === 'right') {
        this.changeSlide(this.currentSharingSlideIndex - 1)
      }
    }, false)

    this.loadLookbackState()
    this.loadCurrentSharingSlideIndex()
  },
  methods: {
    ...mapActions(['setMaxLookbackSlideIndexAction']),
    loadLookbackState() {
      const maxLookbackSlideIndex = storage.getValueByPresentation(this.presentation.id, 'maxLookbackSlideIndex')

      if (maxLookbackSlideIndex !== null && maxLookbackSlideIndex >= 0) {
        this.setMaxLookbackSlideIndexAction(maxLookbackSlideIndex)
      }
    },
    loadCurrentSharingSlideIndex() {
      const currentSlideIndex = storage.getValueByPresentation(this.presentation.id, 'currentSharingSlideIndex')

      if (currentSlideIndex !== null && currentSlideIndex >= 0) {
        this.currentSharingSlideIndex = currentSlideIndex
      }
    },
    onClickPreviousSlide() {
      this.changeSlide(this.currentSharingSlideIndex - 1)
      Util.ga4Track('Presentation Lookback > Previous Slide', {
        presentationId: this.presentation.id,
      })
    },
    onClickNextSlide() {
      this.changeSlide(this.currentSharingSlideIndex + 1, true)
      Util.ga4Track('Presentation Lookback > Next Slide', {
        presentationId: this.presentation.id,
      })
    },
    changeSlide(targetSlideIndex, isNextSlide = false) {
      if (targetSlideIndex < 0) return

      if (isNextSlide && this.isNextSlideDisabled) return

      this.currentSharingSlideIndex = targetSlideIndex

      storage.setValueByPresentation(this.presentation.id, 'currentSharingSlideIndex', targetSlideIndex)

      this.$nextTick(() => {
        this._postMessage(this.slides[this.currentSharingSlideIndex])
      })
    },
    _postMessage(slide) {
      if (!slide || !slide.id) return

      const iframe = document.getElementById('shared-presentation')
      const iframeContentWindow = iframe && iframe.contentWindow

      if (!iframeContentWindow) return

      iframeContentWindow.postMessage({
        type: EVENT_CHANGE_SLIDE,
        slide,
      }, '*')
    },
    onSharingViewLoad() {
      setTimeout(() => {
        this.isSharingViewLoaded = true

        this._postMessage(this.slides[this.currentSharingSlideIndex])
      }, 3000)
    },
  },
}
</script>

<style lang="scss" scoped>
.sharing-view {
  margin-top: 24px;
  margin-bottom: 24px;

  .container {
    max-width: 665px;
    margin: 0 auto;
  }

  iframe {
    border: none !important;
  }
}

.sharing-view-content {
  background-color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px solid #eee;
}

.iframe-placement {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.navigator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  .action {
    display: flex;
    cursor: pointer;
    padding: 6px;
    color: #0A0A0A;

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .prev {
      transform: rotate(90deg);
    }

    .next {
      transform: rotate(-90deg);
    }
  }

  .number {
    font-size: 14px;
    font-weight: 700;
    line-height: 0.5; // life-hack to vertical center the number
    color: #191F2D;
    width: 32px;
    text-align: center;
    margin-left: 12px;
    margin-right: 12px;
  }
}
</style>
